import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ENDPOINT_ROOT } from 'src/Api/Endpoint';
import { useAxiosV2 } from 'src/hooks/useAxiosV2';
import useCustomerFeature from 'src/hooks/useCustomerFeature';

export interface HierarchicalCategoryValueDto {
    id: string;
    name: string;
    depth: number;
    parentId: string | null;
    children: HierarchicalCategoryValueDto[];
}

interface HierarchicalCategoryContextType {
    areaNameById: Record<string, string>;
    areaIdByName: Record<string, string>;
    loaded: boolean;
}

const HierarchicalCategoryContext = createContext<HierarchicalCategoryContextType>({
    areaNameById: {},
    areaIdByName: {},
    loaded: false,
});

// Va montato in alto nell'albero (accanto a dove è disponibile user.customerFeatures),
// così la mappa id<->nome è già pronta sia per la Welcome page (link) sia per
// Location Status (titolo), senza flash dell'id e senza doppie fetch.
export function HierarchicalCategoryProvider({ children }: { children: ReactNode }) {
    const { locHierCategoryId } = useCustomerFeature('LOCATION_STATUS');
    const [areaNameById, setAreaNameById] = useState<Record<string, string>>({});
    const [areaIdByName, setAreaIdByName] = useState<Record<string, string>>({});
    const [loaded, setLoaded] = useState(false);
    const { data, loaded: fetchLoaded, axiosFetch } = useAxiosV2<HierarchicalCategoryValueDto[]>();

    useEffect(() => {
        if (!locHierCategoryId) return;
        axiosFetch({
            url: `${ENDPOINT_ROOT.hierarchicalCategory}/hierid/${locHierCategoryId}`,
            method: 'get',
        });
    }, [locHierCategoryId]);

    useEffect(() => {
        if (!fetchLoaded || !data) return;
        const byId: Record<string, string> = {};
        const byName: Record<string, string> = {};
        data.forEach((item) => {
            byId[item.id] = item.name;
            byName[item.name] = item.id;
        });
        setAreaNameById(byId);
        setAreaIdByName(byName);
        setLoaded(true);
    }, [data, fetchLoaded]);

    return (
        <HierarchicalCategoryContext.Provider value={{ areaNameById, areaIdByName, loaded }}>
            {children}
        </HierarchicalCategoryContext.Provider>
    );
}

export default function useHierarchicalCategoryContext() {
    return useContext(HierarchicalCategoryContext);
}